import { useEffect, useMemo, useState } from 'react'
import type { ProjectSummary } from '@ai-meeting/shared'
import { fetchProjectOverview } from '@/services/projects'
import { PROJECTS_PAGE_SIZE, matchesProjectQuery, visibleRange } from './projectDirectory'

/**
 * What the screen is showing. `connect` and `reauth` are not failures — they
 * are states the reader can act on, and they arrive on a successful response.
 */
export type ProjectDirectoryState = 'loading' | 'ready' | 'error' | 'connect' | 'reauth'

export interface UseProjectDirectoryResult {
  state: ProjectDirectoryState
  /** Server message when `state` is `error`; null when the request never reached one. */
  errorMessage: string | null
  /** Everything the reader can see, before the search box narrows it. */
  projects: ProjectSummary[]
  query: string
  setQuery: (value: string) => void
  /** Projects answering the current search, across every page. */
  matches: ProjectSummary[]
  /** The slice drawn as cards. */
  visible: ProjectSummary[]
  page: number
  setPage: (page: number) => void
  pageCount: number
  range: { start: number; end: number }
}

/**
 * Loads the project directory and owns the search box and the pager.
 *
 * Both are local: the whole list is one Backlog response of a few dozen rows,
 * so narrowing and paging it here costs nothing and spares a round trip per
 * keystroke. If a space ever grows past that, this is the seam to move server
 * side — the screen reads `matches` and `visible` and would not change.
 */
export function useProjectDirectory(): UseProjectDirectoryResult {
  const [projects, setProjects] = useState<ProjectSummary[]>([])
  const [state, setState] = useState<ProjectDirectoryState>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [query, setQueryValue] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    let active = true

    void fetchProjectOverview()
      .then((response) => {
        if (!active) return
        setProjects(response.projects)
        if (response.status === 'CONNECTION_REQUIRED') return setState('connect')
        if (response.status === 'REAUTH_REQUIRED') return setState('reauth')
        setState('ready')
      })
      .catch((error: unknown) => {
        if (!active) return
        setErrorMessage(error instanceof Error ? error.message : null)
        setState('error')
      })

    return () => {
      active = false
    }
  }, [])

  const matches = useMemo(
    () => projects.filter((project) => matchesProjectQuery(project, query)),
    [projects, query]
  )

  const pageCount = Math.ceil(matches.length / PROJECTS_PAGE_SIZE)

  // Not clamped: the pager only offers pages that exist, and the one thing
  // that can shorten the list under the reader's feet — typing in the search
  // box — sends them back to page one.
  const visible = useMemo(
    () => matches.slice((page - 1) * PROJECTS_PAGE_SIZE, page * PROJECTS_PAGE_SIZE),
    [matches, page]
  )

  /** Narrowing the list sends the reader back to its first page, never a stale one. */
  function setQuery(value: string) {
    setQueryValue(value)
    setPage(1)
  }

  return {
    state,
    errorMessage,
    projects,
    query,
    setQuery,
    matches,
    visible,
    page,
    setPage,
    pageCount,
    range: visibleRange(page, matches.length),
  }
}
